import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import type { StreamStatus } from "../../../../stream";
import type { ScoutAttachment, ScoutOptimisticAttachmentPreview } from "../scoutSubmitAttachments";
import type { ScoutActiveTurn, ScoutChatMessage, ScoutThreadSummary } from "./types";

import {
  buildScoutPrivateDocumentExtractionWatchPartUrl,
  formatScoutExtractionWatchUserMessage,
} from "../Chat/scoutExtractionWatchMessage";
import { type ScoutConversationControllerSnapshot } from "./scoutConversationController";
import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import {
  buildTenantDocumentProposalEventMessage,
  parseRespondedTenantDocumentProposalsFromMessages,
  type TenantDocumentProposalEventStatus,
} from "./scoutTenantDocumentProposalEvents";
import { useScoutConversationController } from "./useScoutConversationController";
import {
  type ScoutConversationPrivateDocumentRow,
  useScoutConversationPrivateDocuments,
} from "./useScoutConversationPrivateDocuments";

export { SCOUT_INTERNAL_EVENT_PREFIX } from "./scoutTenantDocumentProposalEvents";

type UseScoutConversationOptions = {
  tenantId: string | undefined;
  conversationId: string | undefined;
  onConversationIdChange?: (conversationId: string) => void;
};

type SendScoutMessageOptions = {
  conversationId?: string;
  allowMutations?: boolean;
  pageContext?: string;
  attachmentIds?: string[];
  extractionWatchPrivateDocumentIds?: string[];
  attachmentPreviews?: ScoutOptimisticAttachmentPreview[];
};

type UseScoutConversationResult = {
  conversation: ScoutThreadSummary | undefined;
  conversationId: string | undefined;
  conversations: ScoutThreadSummary[];
  messages: ScoutChatMessage[];
  activeTurn: ScoutActiveTurn | undefined;
  loading: boolean;
  error: Error | undefined;
  streamStatus: StreamStatus | undefined;
  isSending: boolean;
  isAborting: boolean;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
  sendMessage: (content: string, options?: SendScoutMessageOptions) => Promise<string | undefined>;
  abortTurn: () => Promise<boolean>;
  refetch: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  largeDocumentJob: ScoutConversationPrivateDocumentRow | undefined;
  largeDocumentOptIns: ScoutConversationPrivateDocumentRow[];
  privateDocuments: ScoutConversationPrivateDocumentRow[];
  dismissLargeDocumentOptIn: (privateDocumentIds: string[]) => Promise<void>;
  confirmLargeDocumentAnalysis: (
    input:
      | {
          privateDocumentIds: string[];
          documents: ScoutConversationPrivateDocumentRow[];
        }
      | string
  ) => Promise<void>;
  isConfirmingLargeDocumentAnalysis: boolean;
  isDecliningLargeDocumentAnalysis: boolean;
  registerComposerAttachments: (attachments: ScoutAttachment[]) => void;
  unregisterComposerAttachment: (privateDocumentId: string) => Promise<void>;
  respondedTenantDocumentProposals: Record<string, "confirmed" | "declined">;
  isProcessingTenantDocumentProposal: boolean;
  confirmTenantDocumentProposal: (input: {
    proposalId: string;
    documentIds: string[];
    totalProposedCount: number;
  }) => Promise<void>;
  declineTenantDocumentProposal: (input: {
    proposalId: string;
    totalProposedCount: number;
  }) => Promise<void>;
};

const EMPTY_CONTROLLER_SNAPSHOT: ScoutConversationControllerSnapshot = {
  storeSnapshot: {
    conversationsById: {},
    messagesByConversationId: {},
    activeTurnsByConversationId: {},
    streamStatusByConversationId: {},
  },
  runtimesByConversationId: {},
  reconciledConversationIdsByConversationId: {},
};

const EMPTY_RUNTIME_SNAPSHOT = {
  loading: false,
  error: undefined,
  isSending: false,
  isAborting: false,
  isLoadingOlderMessages: false,
  hasOlderMessages: false,
};

/**
 * React-facing adapter over the shared Scout controller + store.
 *
 * The hook owns only view selection concerns:
 * - which conversation this consumer is currently displaying
 * - when to attach/detach that displayed conversation from the shared controller
 *
 * The hook does not own Scout's runtime state machine. History loading, SSE,
 * optimistic reconciliation, and abort handling all live in the controller.
 */
export function useScoutConversation({
  tenantId,
  conversationId,
  onConversationIdChange,
}: UseScoutConversationOptions): UseScoutConversationResult {
  const controller = useScoutConversationController();
  const controllerSnapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    () => EMPTY_CONTROLLER_SNAPSHOT
  );

  const storeSnapshot = controllerSnapshot.storeSnapshot;
  const [displayConversationId, setDisplayConversationId] = useState(conversationId);

  // Route changes still drive the local display target for this hook instance.
  useEffect(() => {
    setDisplayConversationId(conversationId);
  }, [conversationId]);

  // Attaches just the conversation this hook is currently rendering to the
  // shared controller. Other hook instances can attach other conversations at
  // the same time.
  useEffect(() => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    controller.attachConversation({
      tenantId,
      conversationId: displayConversationId,
    });

    return () => {
      controller.detachConversation(displayConversationId);
    };
  }, [controller, displayConversationId, tenantId]);

  // During first-send optimistic startup, the controller may have already moved
  // state from the optimistic id to the persisted id before this hook has
  // updated its local display id. This alias keeps rendering stable through that
  // handoff.
  const effectiveConversationId =
    (displayConversationId
      ? controllerSnapshot.reconciledConversationIdsByConversationId[displayConversationId]
      : undefined) ?? displayConversationId;

  const conversations = Object.values(storeSnapshot.conversationsById)
    .filter((conversation) => !tenantId || conversation.tenantId === tenantId)
    .sort((first, second) => (second.updatedAt ?? "").localeCompare(first.updatedAt ?? ""));
  const conversation = effectiveConversationId
    ? storeSnapshot.conversationsById[effectiveConversationId]
    : undefined;
  const messages = effectiveConversationId
    ? (storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [])
    : [];
  const activeTurn = effectiveConversationId
    ? storeSnapshot.activeTurnsByConversationId[effectiveConversationId]
    : undefined;
  const streamStatus = effectiveConversationId
    ? storeSnapshot.streamStatusByConversationId[effectiveConversationId]
    : undefined;
  const runtime = effectiveConversationId
    ? (controllerSnapshot.runtimesByConversationId[effectiveConversationId] ??
      EMPTY_RUNTIME_SNAPSHOT)
    : EMPTY_RUNTIME_SNAPSHOT;

  // const persistedConversationId = resolvePersistedScoutConversationId(
  //   effectiveConversationId,
  //   controllerSnapshot.reconciledConversationIdsByConversationId
  // );

  const {
    documents: privateDocuments,
    largeDocumentJob,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmFullAnalysis,
    isConfirming: isConfirmingLargeDocumentAnalysis,
    isDeclining: isDecliningLargeDocumentAnalysis,
    refreshPrivateDocuments,
    trackLargeDocumentAttachments,
    removeTrackedLargeDocumentAttachments,
    ingestTenantDocuments,
    isIngestingTenantDocuments,
  } = useScoutConversationPrivateDocuments({
    tenantId,
    conversationId: effectiveConversationId,
  });

  const [respondedTenantDocumentProposals, setRespondedTenantDocumentProposals] = useState<
    Record<string, "confirmed" | "declined">
  >({});

  const sendMessage = useCallback(
    async (content: string, options?: SendScoutMessageOptions) => {
      if (!tenantId) {
        throw new Error("Scout requires an organization before sending a message.");
      }

      const targetConversationId = options?.conversationId ?? effectiveConversationId;

      if (options?.attachmentPreviews?.length) {
        trackLargeDocumentAttachments(
          options.attachmentPreviews.map((preview) => ({
            privateDocumentId: preview.privateDocumentId,
            filename: preview.filename,
            sizeBytes: preview.sizeBytes,
            ingestionMode: preview.ingestionMode,
            extractionStatus: preview.extractionStatus,
          }))
        );
      }

      const { optimisticConversationId, completion } = controller.sendMessage({
        tenantId,
        conversationId: targetConversationId,
        content,
        allowMutations: options?.allowMutations,
        ...(options?.pageContext ? { pageContext: options.pageContext } : {}),
        attachmentIds: options?.attachmentIds,
        attachmentPreviews: options?.attachmentPreviews,
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });

      if (targetConversationId && !isScoutOptimisticConversationId(targetConversationId)) {
        void refreshPrivateDocuments(targetConversationId);
      }

      // For a brand new conversation, switch the UI immediately to the
      // optimistic conversation id so the user sees their message and the local
      // Thinking state without waiting for navigation.
      if (!targetConversationId) {
        setDisplayConversationId(optimisticConversationId);
      }

      const resolvedConversationId = await completion;
      if (resolvedConversationId && resolvedConversationId !== targetConversationId) {
        setDisplayConversationId(resolvedConversationId);
        onConversationIdChange?.(resolvedConversationId);
      }

      await refreshPrivateDocuments(resolvedConversationId ?? targetConversationId ?? undefined);

      return resolvedConversationId;
    },
    [
      controller,
      effectiveConversationId,
      onConversationIdChange,
      refreshPrivateDocuments,
      tenantId,
      trackLargeDocumentAttachments,
    ]
  );

  const abortTurn = useCallback(async () => {
    if (!tenantId || !effectiveConversationId) {
      return false;
    }

    return controller.abortTurn({
      tenantId,
      conversationId: effectiveConversationId,
    });
  }, [controller, effectiveConversationId, tenantId]);

  const refetch = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    await controller.refetchConversation({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  const confirmLargeDocumentAnalysis = useCallback(
    async (
      input:
        | {
            privateDocumentIds: string[];
            documents: ScoutConversationPrivateDocumentRow[];
          }
        | string
    ) => {
      const privateDocumentIds =
        typeof input === "string" ? [input] : Array.from(new Set(input.privateDocumentIds));
      if (privateDocumentIds.length === 0) {
        return;
      }

      const documentsById = new Map(
        (typeof input === "string" ? [] : input.documents).map((document) => [
          document.id,
          document,
        ])
      );

      await confirmFullAnalysis(privateDocumentIds);

      await sendMessage(formatScoutExtractionWatchUserMessage(privateDocumentIds), {
        attachmentIds: privateDocumentIds,
        attachmentPreviews: privateDocumentIds.map((privateDocumentId) => {
          const document = documentsById.get(privateDocumentId);
          return {
            privateDocumentId,
            filename: document?.filename ?? "Document.pdf",
            mediaType: "application/pdf",
            sizeBytes: document?.sizeBytes ?? 0,
            ingestionMode:
              document?.ingestionMode ?? ScoutPrivateDocumentIngestionMode.LargePdfExtraction,
            extractionStatus: ScoutPrivateDocumentExtractionStatus.Pending,
            filePartUrl: buildScoutPrivateDocumentExtractionWatchPartUrl(privateDocumentId),
          };
        }),
        extractionWatchPrivateDocumentIds: privateDocumentIds,
      });
    },
    [confirmFullAnalysis, sendMessage]
  );

  const confirmTenantDocumentProposal = useCallback(
    async (input: { proposalId: string; documentIds: string[]; totalProposedCount: number }) => {
      const uniqueDocumentIds = [...new Set(input.documentIds)];
      if (uniqueDocumentIds.length === 0) {
        return;
      }

      const { privateDocumentIds, privateDocuments } =
        await ingestTenantDocuments(uniqueDocumentIds);

      const synthMessage = buildTenantDocumentProposalEventMessage({
        status: "confirmed",
        proposalId: input.proposalId,
        count: privateDocumentIds.length,
        totalProposedCount: input.totalProposedCount,
      });

      // Surface extraction-watch attachments for every newly-created
      // PrivateDocument so the existing inline progress card renders in the
      // chat (matches the direct upload flow — Meredith's request). Cards
      // for non-large-PDF docs render as no-ops via the card's visibility
      // check, so it's safe to include them all without filtering by mode.
      const watchIds = privateDocumentIds.length > 0 ? privateDocumentIds : undefined;
      const documentById = new Map(privateDocuments.map((document) => [document.id, document]));
      const attachmentPreviews = watchIds?.map((privateDocumentId) => {
        const document = documentById.get(privateDocumentId);
        return {
          privateDocumentId,
          filename: document?.filename ?? "document",
          mediaType: document?.mediaType ?? "application/octet-stream",
          sizeBytes: document?.sizeBytes ?? 0,
          ingestionMode:
            document?.ingestionMode ?? ScoutPrivateDocumentIngestionMode.LargePdfExtraction,
          extractionStatus:
            document?.extractionStatus ?? ScoutPrivateDocumentExtractionStatus.Pending,
          filePartUrl: buildScoutPrivateDocumentExtractionWatchPartUrl(privateDocumentId),
        };
      });

      await sendMessage(synthMessage, {
        attachmentIds: watchIds,
        extractionWatchPrivateDocumentIds: watchIds,
        attachmentPreviews,
      });

      setRespondedTenantDocumentProposals((current) => ({
        ...current,
        [input.proposalId]: "confirmed",
      }));
    },
    [ingestTenantDocuments, sendMessage]
  );

  const declineTenantDocumentProposal = useCallback(
    async (input: { proposalId: string; totalProposedCount: number }) => {
      const synthMessage = buildTenantDocumentProposalEventMessage({
        status: "declined",
        proposalId: input.proposalId,
        count: input.totalProposedCount,
        totalProposedCount: input.totalProposedCount,
      });

      await sendMessage(synthMessage);

      setRespondedTenantDocumentProposals((current) => ({
        ...current,
        [input.proposalId]: "declined",
      }));
    },
    [sendMessage]
  );

  const registerComposerAttachments = useCallback(
    (attachments: ScoutAttachment[]) => {
      trackLargeDocumentAttachments(
        attachments.map((attachment) => ({
          privateDocumentId: attachment.privateDocumentId,
          filename: attachment.filename,
          sizeBytes: attachment.sizeBytes,
          ingestionMode: attachment.ingestionMode,
          extractionStatus: attachment.extractionStatus,
        }))
      );
      void refreshPrivateDocuments(effectiveConversationId ?? undefined);
    },
    [effectiveConversationId, refreshPrivateDocuments, trackLargeDocumentAttachments]
  );

  const unregisterComposerAttachment = useCallback(
    async (privateDocumentId: string) => {
      removeTrackedLargeDocumentAttachments([privateDocumentId]);
      await refreshPrivateDocuments(effectiveConversationId ?? undefined);
    },
    [effectiveConversationId, refreshPrivateDocuments, removeTrackedLargeDocumentAttachments]
  );

  const loadOlderMessages = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    await controller.loadOlderMessages({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  // Rehydrate "already responded" state from the persisted message history so
  // the proposal card doesn't re-prompt after a page refresh. In-session state
  // overrides history (covers the brief window between click and the synth
  // message round-tripping back into `messages`). Keyed on the underlying
  // store map rather than the local `messages` variable, which creates a new
  // empty array on each render when no conversation is active.
  const respondedProposalsFromHistory = useMemo<
    Record<string, TenantDocumentProposalEventStatus>
  >(() => {
    if (!effectiveConversationId) return {};
    const messagesForConversation =
      storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [];
    return parseRespondedTenantDocumentProposalsFromMessages(messagesForConversation);
  }, [effectiveConversationId, storeSnapshot.messagesByConversationId]);

  const mergedRespondedTenantDocumentProposals = useMemo(
    () => ({
      ...respondedProposalsFromHistory,
      ...respondedTenantDocumentProposals,
    }),
    [respondedProposalsFromHistory, respondedTenantDocumentProposals]
  );

  return {
    conversation,
    conversationId: displayConversationId,
    conversations,
    messages,
    activeTurn,
    loading: runtime.loading,
    error: runtime.error,
    streamStatus,
    isSending: runtime.isSending,
    isAborting: runtime.isAborting,
    isLoadingOlderMessages: runtime.isLoadingOlderMessages,
    hasOlderMessages: runtime.hasOlderMessages,
    sendMessage,
    abortTurn,
    refetch,
    loadOlderMessages,
    largeDocumentJob,
    largeDocumentOptIns,
    privateDocuments,
    dismissLargeDocumentOptIn,
    confirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    registerComposerAttachments,
    unregisterComposerAttachment,
    respondedTenantDocumentProposals: mergedRespondedTenantDocumentProposals,
    isProcessingTenantDocumentProposal: isIngestingTenantDocuments,
    confirmTenantDocumentProposal,
    declineTenantDocumentProposal,
  };
}
